// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";

import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VideoContainer from "@werner/App/Components/Media/VideoContainer";
import setSelectedVideoIndex from "@werner/App/Handlers/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@werner/App/Store/Reducers";
import {
    getProductVideos,
    getSelectedVideo,
    getSelectedVideoIndex,
} from "@werner/App/Store/Selectors/ProductSelectors";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@werner/Overrides/Components/ProductImageCarousel";
import Expand1 from "@werner/XDL/Icons/Expand1";
import { color, mergeWithCss } from "@werner/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    selectedImage: getSelectedImage(state, ownProps.productContext.product),
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    // XNG-Change
    selectedVideo: getSelectedVideo(state, ownProps.productContext.product),
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.productContext.product),
    videos: getProductVideos(state, ownProps.productContext.product),
    customSettings: getSettingsCollection(state).customSettings,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    // XNG-Change
    setSelectedVideoIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Change
    videoContainer?: InjectableCss;
    mediaContainer?: InjectableCss;
    expandLink?: ClickablePresentationProps;
    textBadge?: InjectableCss;
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
                height: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    videoContainer: {
        css: css`
            margin-bottom: 20px;
            &,
            & > div {
                height: 412px;
                width: 412px;
                max-width: 100%;
            }
            &&&& video,
            iframe {
                cursor: pointer;
                max-height: 412px;
                min-height: 412px;
            }
        `,
    },
    mediaContainer: {
        css: css`
            position: relative;
        `,
    },
    expandLink: {
        css: css`
            width: 24px;
            height: 24px;
            padding: 4px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: ${color("common.ui2")};
            position: absolute;
            top: 10px;
            left: 10px;
            z-index: 3;
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    selectedVideo,
    selectedVideoIndex,
    setSelectedVideoIndex,
    videos,
    customSettings,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        if (videos && videos.length > 0) {
            setSelectedVideoIndex({ index: -1, product });
        }
        setSelectedImageIndex({ index });
    };

    const selectVideoHandler = (index: number) => {
        setSelectedImageIndex({ index: -1 });
        setSelectedVideoIndex({ index, product });
    };

    const { isMobileScreen } = useAppSelector(state => ({
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const video = selectedVideo;

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    const path = image.mediumImagePath || image.largeImagePath;

    if (!path) {
        return null;
    }

    const videoSection = video && video.link && (
        <StyledWrapper {...styles.videoContainer}>
            <VideoContainer
                src={video.link as string}
                isYoutubeVideo={video.isYoutubeVideo}
                extension={getFileExtension(video.link as string)}
                controls={true}
                insideModal={fullPrimaryImageModalIsOpen}
            ></VideoContainer>
        </StyledWrapper>
    );

    const showProductImageCarousel =
        (product.images && product.images.length > 1) ||
        (product.images && product.images.length > 0 && !!videos?.length);

    const primaryImageStyle: ProductPrimaryImageStyles = mergeWithCss(styles.primaryImage, {
        img: {
            width: isMobileScreen ? 413 : product.isVariantParent ? customSettings?.parentProductImageDimension : 412,
            height: isMobileScreen ? 413 : product.isVariantParent ? customSettings?.parentProductImageDimension : 412,
        },
    });

    return (
        <StyledWrapper {...styles.mediaContainer}>
            <Clickable onClick={imageClickHandler} {...styles.expandLink} aria-label="click to expand the image">
                <Icon src={Expand1} size={16} />
            </Clickable>
            {videoSection}
            {!video && (
                <>
                    {!showOverlayBadge && (
                        <ProductPrimaryImage
                            product={product}
                            image={image}
                            onClick={imageClickHandler}
                            extendedStyles={primaryImageStyle}
                            key={image.id}
                            useLargeImage={!isMobileScreen}
                        />
                    )}
                    {showOverlayBadge && overlayBadge && (
                        <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                            <ProductPrimaryImage
                                product={product}
                                image={image}
                                onClick={imageClickHandler}
                                extendedStyles={primaryImageStyle}
                                key={image.id}
                                useLargeImage={!isMobileScreen}
                            />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </>
            )}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                {videoSection}
                {!video && (
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                        key={image.id}
                        useLargeImage={!isMobileScreen}
                    />
                )}
                {showProductImageCarousel && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                        selectedVideoIndex={selectedVideoIndex}
                        onSelectVideo={selectVideoHandler}
                    />
                )}
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
